import { startTransition, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  PaginationType,
  SetPaginationCountType,
} from "src/components/TablePaginationLimitChanger/hooks/useTablePaginationLimitChanger";
import { useSetPaginationCount } from "src/hooks/useSetPaginationCount";
import { RoutePaths } from "src/pages/routePaths";
import { useGetProductList } from "../api/queries";
import { useGetAccessPermission } from "src/hooks/useGetAccessPermission";
import { AccessResource, ActionType } from "src/types/accessControl";
import { SelectOptionsType } from "src/types";
import { accessControl } from "src/constant";
import { convertFilterToParams, convertOptionsToParams, convertStringToParams, getFilterParam, getOptionParam, getStringParam } from "src/utils/convertToSearchParams";

export interface FilterType {
  productCategory: SelectOptionsType;
  productSubCategory: SelectOptionsType;
  productName: SelectOptionsType;
  productVariant: SelectOptionsType;
  availability: SelectOptionsType;
  minPrice: number | null;
  maxPrice: number | null;
  productStatus: SelectOptionsType;
  availableStock: SelectOptionsType;
}

export const productListFilterDefaultValue = {
  productCategory: [],
  productSubCategory: [],
  productName: [],
  productVariant: [],
  availability: [],
  minPrice: null,
  maxPrice: null,
  productStatus: [],
  availableStock: [],
};

export const sortOptions = [
  {
    label: "Most Available Stock",
    value: "most-available",
  },
  {
    label: "Least Available Stock",
    value: "least-available",
  },
  {
    label: "Product Name A-Z",
    value: "name-asc",
  },
  {
    label: "Product Name Z-A",
    value: "name-desc",
  },
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const productAndPackageSortValue = [
  "most-available",
  "least-available",
  "name-asc",
  "name-desc",
] as const;

export type ProductAndPackageSortValueType =
  (typeof productAndPackageSortValue)[number];

export const getProductAndPackageSortingParams = (
  type: ProductAndPackageSortValueType
) => {
  let params = {};

  if (type === "most-available") {
    params = {
      sort_by: "stock",
      sort_order: "desc",
    };
  } else if (type === "least-available") {
    params = {
      sort_by: "stock",
      sort_order: "asc",
    };
  } else if (type === "name-asc") {
    params = {
      sort_by: "name",
      sort_order: "asc",
    };
  } else if (type === "name-desc") {
    params = {
      sort_by: "name",
      sort_order: "desc",
    };
  }

  return params;
};

interface UseProductListHandlerProps {
  pagination: PaginationType;
  setCount: SetPaginationCountType;
}

const useProductListHandler = ({
  pagination,
  setCount,
}: UseProductListHandlerProps) => {
  const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

  const isAllowToCreate = useGetAccessPermission({
    resource: AccessResource["Product Management - Product List"],
    action: ActionType["Create"],
  });
  const isAllowedProductCategory = useGetAccessPermission({
    resource: AccessResource["Master Data - Product Category"],
    action: ActionType["Read"],
  });
  const isAllowedItemStockUnit = useGetAccessPermission({
    resource: AccessResource["Master Data - Item Stock Unit"],
    action: ActionType["Read"],
  });
  const hasAllAccess = isAllowToCreate && isAllowedProductCategory && isAllowedItemStockUnit;

  const initialSearchInputValue = getStringParam(
    searchParams,
    "productSearch",
    ""
  );

  const initialSort = getOptionParam(
    searchParams,
    "productSort",
    sortOptions.map((o) => o.value),
    sortOptions[0].value
  ) as ProductAndPackageSortValueType;

  const initialFilter = getFilterParam<FilterType>(
    searchParams,
    "productFilter",
    productListFilterDefaultValue
  );

  const [searchByProductNameOrProductSKU, setSearchByProductNameOrProductSKUState] =
    useState(initialSearchInputValue);
  const [sort, setSortState] =
    useState<ProductAndPackageSortValueType>(initialSort);
  const [filter, setFilterState] = useState<FilterType>(initialFilter);

  const { data, isLoading } = useGetProductList({
    ...pagination,
    productNameOrProductSKU: searchByProductNameOrProductSKU,
    sortBy: sort,
    ...filter,
  });

  const setSearchByProductNameOrProductSKU = convertStringToParams(
    "productSearch",
    setSearchByProductNameOrProductSKUState,
    setSearchParams
  );

  const setSort = convertOptionsToParams(
    "productSort",
    setSortState as React.Dispatch<React.SetStateAction<any>>,
    setSearchParams
  );

  const setFilter = convertFilterToParams<FilterType>(
    "productFilter",
    setFilterState,
    setSearchParams,
    (value) =>
      Object.values(value).some((v) =>
        Array.isArray(v) ? v.length > 0 : v !== null && v !== undefined && v !== ""
      )
  );

  const handleAdd = () =>
    startTransition(() =>
      navigate(RoutePaths.ProductManagementCreateProductList)
    );

  useSetPaginationCount({ meta: data?.meta, setCount });

  return {
    isAllowedProductCategory,
    isAllowedItemStockUnit,
    hasAllAccess,
    isLoading,
    isAllowToCreate,
    productList: data?.data,
    totalCount: data?.meta.total ?? 0,
    handleAdd,
    searchByProductNameOrProductSKU,
    setSearchByProductNameOrProductSKU,
    sort,
    setSort,
    sortOptions,
    filter,
    setFilter,
    hasFilter:
      searchByProductNameOrProductSKU !== "" ||
      Object.values(filter).some((filter) =>
        Array.isArray(filter) ? filter.length > 0 : filter
      ),
  };
};

export default useProductListHandler;
